import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Pagination } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { Checkbox } from '@/design-system/checkbox';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Tag } from '@/design-system/tag';
import { toast } from '@/design-system/toaster';
import { useUrlNumber, useUrlSearch } from '@/hooks/urlState';

import { warehouseApi } from '../../api/warehouse.api';
import { DateField } from '../../components/DateField';
import { FormField, TextArea, TextInput } from '../../components/form';
import { EmptyTable, ListSearch } from '../../components/ListShell';
import {
  PoSourceCell,
  PoSourceKVs,
  PoTypeTag,
} from '../../components/orderType';
import {
  codeCls,
  dateCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  KVGrid,
  linkCls,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  ProgressCell,
  Section,
} from '../../components/ui';
import {
  DocumentUpload,
  type DocumentValue,
} from '../../components/DocumentUpload';
import { StorageSelect } from '../../components/StorageSelect';
import { WizardFooter, WizardSteps } from '../../components/wizard';
import {
  fmtDate,
  fmtDateTime,
  fmtQty,
  norm,
  nowLocalInput,
  sum,
} from '../../format';
import { MAX_LIMIT, PAGE_SIZE_OPTIONS } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { type FieldTarget, useServerErrors } from '../../hooks/useServerErrors';
import { storageLabel, useStorageLabels } from '../../hooks/useStorages';
import { useWizard } from '../../hooks/useWizard';
import { RECEIPT_STATUS } from '../../status';
import type {
  FieldError,
  PurchaseOrder,
  PurchaseOrderDetail,
  WarehouseStorage,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

interface DraftLine {
  key: number;
  sku: string;
  qty: string;
  /** YYYY-MM-DD; required on every received line (the server builds the batch from it). */
  expiry: string;
  storageID: string;
}

/** Step 1 is the inbound pick list (no `?po=`); 2–4 live in ReceiptForm. */
const STEPS = [
  { id: 1, label: 'Inbound request' },
  { id: 2, label: 'Put-away' },
  { id: 3, label: 'Penerimaan' },
  { id: 4, label: 'Review' },
];

let seq = 0;

/** Storages the default destinations rotate over (the first of the tree). */
const DEFAULT_STORAGES = 20;
const NO_STORAGES: WarehouseStorage[] = [];

/** One draft line per PO line still open; destination filled in later. */
export const seedDraftLines = (
  poLines: PurchaseOrderDetail['lines']
): DraftLine[] =>
  poLines
    .filter((l) => l.qtyRemaining > 0)
    .map((l) => ({
      key: ++seq,
      sku: l.sku,
      qty: String(l.qtyRemaining),
      expiry: '',
      storageID: '',
    }));

/**
 * Default destinations rotate over `defaults` by line position — but only
 * for lines without one: a bin the user already picked is never replaced
 * (storages loading late must not wipe put-away edits).
 */
export const fillMissingStorage = (
  lines: DraftLine[],
  defaults: WarehouseStorage[]
): DraftLine[] => {
  if (!defaults.length || lines.every((l) => l.storageID)) return lines;
  return lines.map((l, i) =>
    l.storageID ? l : { ...l, storageID: defaults[i % defaults.length].id }
  );
};

type GrFieldKey =
  | 'lines'
  | 'arrival'
  | 'deliveryNoteNo'
  | 'deliveryNote'
  | 'notes'
  | 'closesBackorder'
  | 'form';

const GR_STEP_OF: Record<GrFieldKey, number> = {
  lines: 2,
  arrival: 3,
  deliveryNoteNo: 3,
  deliveryNote: 3,
  notes: 3,
  closesBackorder: 3,
  form: 4,
};
const grStepOfKey = (k: GrFieldKey): number => GR_STEP_OF[k];

/** The line row an error names (`lineIndex`, else `lines[2].…`), or null. */
const grLineOf = (e: FieldError): number | null => {
  if (e.lineIndex !== undefined && e.lineIndex !== null) return e.lineIndex;
  const m = /lines?\W*(\d+)/i.exec(e.field || '');
  return m ? Number(m[1]) : null;
};

/** The line inputs that show their own inline error. */
type GrLineField = 'expiredDate';

const grLineFieldOf = (e: FieldError): GrLineField | null =>
  norm(e.field || '').endsWith('expireddate') ? 'expiredDate' : null;

export const DELIVERY_NOTE_REQUIRED =
  'Surat jalan supplier (PDF) wajib diunggah.';

/** Map a backend field (snake, dotted or camel) onto a goods receipt field. */
export const grFieldOf = (e: FieldError): FieldTarget<GrFieldKey> => {
  const raw = e.field || '';
  const n = norm(raw);
  if (!n) return { key: 'form' };
  const line = grLineOf(e);
  if (
    line !== null ||
    n.startsWith('line') ||
    ['sku', 'qty', 'storageid', 'expireddate'].includes(n)
  )
    return { key: 'lines', line };
  if (n.includes('arriv')) return { key: 'arrival' };
  if (n.includes('backorder')) return { key: 'closesBackorder' };
  if (n.includes('photo') || n.includes('url')) return { key: 'deliveryNote' };
  if (n.includes('deliverynote')) return { key: 'deliveryNoteNo' };
  if (n === 'notes') return { key: 'notes' };
  return { key: 'form' };
};

const InlineError: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children,
}) => (
  <p
    id={id}
    className="mt-1 text-[11px] text-(--state-error-base)"
    role="alert"
  >
    {children}
  </p>
);

/* ------------------------- step 1: pick inbound -------------------------- */

const PickInbound: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  // URL-backed like the lists, so Back from a receipt keeps the search/page.
  const search = useUrlSearch('q', { resetPage: true, delay: 350 });
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [rawLimit, setLimit] = useUrlNumber('limit', 20, {
    min: 1,
    resetPage: true,
  });
  const limit = Math.min(MAX_LIMIT, rawLimit);
  // Only requests still waiting for goods, searched and paged by the server.
  const list = useLoad(
    (signal) =>
      warehouseApi.listInboundRequests(
        {
          hubID,
          open: true,
          search: search.committed || null,
          page,
          limit,
        },
        { signal }
      ),
    [hubID, search.committed, page, limit, refreshToken],
    ready
  );
  const rows = list.data?.data ?? [];
  const total = list.data?.pagination.total ?? 0;
  const columns = useMemo<TableDataColumn<PurchaseOrder>[]>(
    () => [
      {
        key: 'code',
        title: 'PO number',
        render: (_v, x) => <span className={codeCls}>{x.code}</span>,
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, x) => (
          <span className={nameCls}>
            {x.client?.name ?? `Client #${x.clientID}`}
          </span>
        ),
      },
      {
        key: 'type',
        title: 'Tipe',
        render: (_v, x) => <PoTypeTag type={x.type} />,
      },
      {
        key: 'source',
        title: 'Sumber',
        render: (_v, x) => <PoSourceCell po={x} />,
      },
      {
        key: 'hub',
        title: 'Hub',
        render: (_v, x) => (
          <span className={nameCls}>{x.hub?.name ?? `Hub #${x.hubID}`}</span>
        ),
      },
      {
        key: 'eta',
        title: 'Est. arrival',
        render: (_v, x) => (
          <span className={dateCls}>{fmtDateTime(x.estArrivalAt)}</span>
        ),
      },
      {
        key: 'received',
        title: 'Received',
        render: (_v, x) => (
          <ProgressCell
            received={x.totalReceived}
            expected={x.totalQty}
            format={fmtQty}
          />
        ),
      },
      {
        key: 'irStatus',
        title: 'Status',
        render: (_v, x) => (
          <MappedBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
        ),
      },
    ],
    []
  );
  return (
    <Section
      title="Pilih inbound request"
      right={
        <ListSearch
          value={search.value}
          onChange={search.setValue}
          placeholder="Search PO, vendor…"
        />
      }
    >
      {list.error ? (
        <LoadError message={list.error.message} onRetry={list.reload} />
      ) : (
        <>
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={rows}
            columns={columns}
            onRowClick={(x) =>
              navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new?po=${x.id}`)
            }
            emptyState={
              <EmptyTable
                title={
                  search.committed
                    ? 'Tidak ada inbound request yang cocok'
                    : 'Tidak ada inbound request yang menunggu barang'
                }
              />
            }
          />
          {total > 0 && (
            <Pagination
              currentPage={page}
              totalItems={total}
              itemsPerPage={limit}
              onPageChange={setPage}
              onPageSizeChange={setLimit}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          )}
        </>
      )}
    </Section>
  );
};

/* ------------------------- step 2: receipt form -------------------------- */

const ReceiptForm: React.FC<{ detail: PurchaseOrderDetail }> = ({ detail }) => {
  const navigate = useNavigate();
  const po = detail.order;
  // Seeded once per PO (the parent keys this form by PO id): a re-fetch of
  // the same PO or the default storages arriving never rebuilds the lines.
  const [lines, setLines] = useState<DraftLine[]>(() =>
    seedDraftLines(detail.lines)
  );
  const [arrivedAt, setArrivedAt] = useState(nowLocalInput());
  const [deliveryNoteNo, setDeliveryNoteNo] = useState('');
  const [deliveryNote, setDeliveryNote] = useState<DocumentValue | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [notes, setNotes] = useState('');
  const [closeBackorder, setCloseBackorder] = useState(false);
  const [busy, setBusy] = useState(false);
  const server = useServerErrors(grFieldOf, grStepOfKey);
  const { errors, setErrors, fieldError: fe } = server;
  /** Line keys in the order the last check / submit saw them (error lineIndex). */
  const checkedKeys = useRef<number[]>([]);

  // Defaults rotate over the first Storage-type bins of the hub (one page, not
  // the whole hub); a hub without bins yet falls back to any Storage-type
  // storage (e.g. receiving into the location first).
  const defaultsLoad = useLoad(
    async (signal) => {
      const base = {
        hubID: po.hubID,
        status: 'ACTIVE',
        type: 'STORAGE',
        page: 1,
        limit: DEFAULT_STORAGES,
      } as const;
      const bins = await warehouseApi.listStoragesPage(
        { ...base, category: 'BIN' },
        { signal }
      );
      if (bins.data.length) return bins.data;
      return (await warehouseApi.listStoragesPage(base, { signal })).data;
    },
    [po.hubID]
  );
  const defaults = useMemo(
    () => defaultsLoad.data ?? NO_STORAGES,
    [defaultsLoad.data]
  );

  // Labels of the chosen storages — the defaults are already known; anything
  // else goes to the server once, by `ids=`.
  const labels = useStorageLabels(
    po.hubID,
    lines.map((l) => l.storageID)
  );
  const rememberStorages = labels.remember;

  // Fill a default destination only where none is set yet.
  useEffect(() => {
    if (!defaults.length) return;
    rememberStorages(defaults);
    setLines((prev) => fillMissingStorage(prev, defaults));
  }, [defaults, rememberStorages]);

  const lineBy = useMemo(
    () => new Map(detail.lines.map((l) => [l.sku, l])),
    [detail.lines]
  );

  const setLine = (key: number, patch: Partial<DraftLine>) =>
    setLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, ...patch } : l))
    );

  /**
   * One more storage for a SKU: a new row under its last one, holding the qty
   * not yet put away (PO remainder − the SKU's rows). When everything is
   * already allocated, the last row is split in half instead. The new row
   * copies the expired date and starts on the next default storage.
   */
  const addStorage = (sku: string) =>
    setLines((prev) => {
      let at = -1;
      prev.forEach((l, i) => {
        if (l.sku === sku) at = i;
      });
      if (at < 0) return prev;
      const last = prev[at];
      const allocated = prev
        .filter((l) => l.sku === sku)
        .reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
      const unallocated =
        Math.round(((lineBy.get(sku)?.qtyRemaining ?? 0) - allocated) * 100) /
        100;
      let lastQty = last.qty;
      let qty: string;
      if (unallocated > 0) {
        qty = String(unallocated);
      } else {
        const total = Number(last.qty) || 0;
        const half = Math.floor(total / 2);
        lastQty = String(half);
        qty = String(total - half);
      }
      const idx = defaults.findIndex((b) => b.id === last.storageID);
      const nextBin = defaults.length
        ? defaults[(idx + 1) % defaults.length].id
        : '';
      const next = [...prev];
      next[at] = { ...last, qty: lastQty };
      next.splice(at + 1, 0, {
        key: ++seq,
        sku,
        qty,
        expiry: last.expiry,
        storageID: nextBin,
      });
      return next;
    });

  const filled = lines.filter((l) => Number(l.qty) > 0);

  const putAwayProblems = (): FieldError[] => {
    const problems: string[] = [];
    if (!filled.length) problems.push('Isi qty diterima minimal satu baris.');
    filled.forEach((l) => {
      if (!l.storageID) problems.push(`${l.sku}: bin tujuan wajib dipilih.`);
    });
    // The expired date is optional (goods without one get a NOEXP batch);
    // two lines on the same SKU + expiry + storage are fine, the server
    // merges them. Server-side line errors still land inline (see errors).
    const inputProblems: FieldError[] = [];
    const totals = new Map<string, number>();
    filled.forEach((l) =>
      totals.set(l.sku, (totals.get(l.sku) ?? 0) + Number(l.qty))
    );
    totals.forEach((q, sku) => {
      const remaining = lineBy.get(sku)?.qtyRemaining ?? 0;
      if (q > remaining)
        problems.push(
          `${sku}: total ${fmtQty(q)} melebihi sisa PO ${fmtQty(remaining)}.`
        );
    });
    return [
      ...problems.map((message) => ({ field: 'lines', message })),
      ...inputProblems,
    ];
  };

  const receiptProblems = (): FieldError[] =>
    deliveryNote
      ? []
      : [{ field: 'deliveryNotePhotoUrl', message: DELIVERY_NOTE_REQUIRED }];

  const wizard = useWizard<FieldError>({
    first: 2,
    total: STEPS.length,
    check: (s) =>
      s === 2 ? putAwayProblems() : s === 3 ? receiptProblems() : [],
  });
  const { step } = wizard;

  const next = () => {
    checkedKeys.current = filled.map((l) => l.key);
    setErrors(wizard.next());
  };

  const back = () => {
    setErrors([]);
    if (!wizard.back()) navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new`);
  };

  /**
   * First error of a draft line (its index in the last checked / submitted
   * body) for one input, or — `field` null — one that has no input of its own.
   */
  const lineErrorOf = (key: number, field: GrLineField | null) => {
    const i = checkedKeys.current.indexOf(key);
    if (i < 0) return null;
    const hit = errors.find(
      (e) => grLineOf(e) === i && grLineFieldOf(e) === field
    );
    return hit?.message ?? null;
  };

  /** Edit a line; the inline error of its expired date goes once the date changes. */
  const editLine = (key: number, patch: Partial<DraftLine>) => {
    setLine(key, patch);
    if (!('expiry' in patch)) return;
    const i = checkedKeys.current.indexOf(key);
    if (i < 0) return;
    setErrors((prev) =>
      prev.filter(
        (e) => !(grLineFieldOf(e) === 'expiredDate' && grLineOf(e) === i)
      )
    );
  };

  const submit = async () => {
    checkedKeys.current = filled.map((l) => l.key);
    const problems = wizard.checkAll();
    if (problems.length) {
      setErrors(problems);
      return;
    }
    setBusy(true);
    setErrors([]);
    try {
      const gr = await warehouseApi.createInboundReceipt(po.id, {
        arrivedAt: arrivedAt ? new Date(arrivedAt).toISOString() : null,
        deliveryNoteNo: deliveryNoteNo.trim() || null,
        deliveryNotePhotoUrl: deliveryNote?.url ?? null,
        closesBackorder: closeBackorder,
        notes: notes.trim(),
        lines: filled.map((l) => ({
          sku: l.sku,
          qty: Number(l.qty),
          expiredDate: l.expiry || null,
          storageID: l.storageID,
        })),
      });
      toast.success('Goods receipt posted', { description: gr.code });
      // The receipt page loads itself.
      navigate(`${WAREHOUSE_PATHS.goodsReceipt}/${gr.id}`);
    } catch (err) {
      // 422 field errors → inline at their line / field, back to that step.
      const shown = server.fromError(err);
      if (shown.step !== null) wizard.goTo(shown.step);
      else setErrors([{ field: '', message: shown.message }]);
    } finally {
      setBusy(false);
    }
  };

  // What stays open on the PO after this receipt, per SKU.
  const remainingAfter = detail.lines
    .filter((l) => l.qtyRemaining > 0)
    .map((l) => ({
      sku: l.sku,
      uom: l.uomName,
      left: Math.max(
        0,
        l.qtyRemaining -
          sum(filled.filter((f) => f.sku === l.sku).map((f) => Number(f.qty)))
      ),
    }))
    .filter((x) => x.left > 0);

  const storagesError = defaultsLoad.error ?? labels.error;
  const reloadStorages = () => {
    defaultsLoad.reload();
    labels.reload();
  };
  const grid =
    'grid grid-cols-[96px_160px_minmax(0,1fr)_40px] items-start gap-2';

  const errorAlert = errors.length > 0 && (
    <Alert
      status="error"
      appearance="stroke"
      size="lg"
      title={`${errors.length} isian perlu diperbaiki`}
      data-testid="gr-errors"
    >
      <ul className="list-disc pl-4">
        {errors.map((e, i) => (
          <li key={`${i}-${e.message}`}>{e.message}</li>
        ))}
      </ul>
    </Alert>
  );

  return (
    <div className="flex flex-col gap-5">
      <WizardSteps steps={STEPS} current={step} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          {step === 2 && (
            <Section
              title="Put-away"
              description={`${po.code} · ${po.supplierName}`}
            >
              <div className="flex flex-col gap-2">
                {errorAlert}
                {storagesError && (
                  // Unknown ≠ none: don't claim the hub has no storage.
                  <Alert
                    status="error"
                    appearance="stroke"
                    size="sm"
                    title={`Storage default gagal dimuat: ${storagesError.message}`}
                    data-testid="gr-storages-error"
                    action={
                      <Button
                        tone="neutral"
                        style="stroke"
                        size="xs"
                        onClick={reloadStorages}
                      >
                        Coba lagi
                      </Button>
                    }
                  />
                )}
                {lines.length === 0 && (
                  <p className={metaCls}>Semua qty PO sudah diterima.</p>
                )}
                {[...new Set(lines.map((l) => l.sku))].map((sku) => {
                  const poLine = lineBy.get(sku);
                  const skuLines = lines.filter((l) => l.sku === sku);
                  const allocated =
                    Math.round(
                      skuLines.reduce((t, l) => t + (Number(l.qty) || 0), 0) *
                        100
                    ) / 100;
                  const remaining = poLine?.qtyRemaining ?? 0;
                  const uom = poLine?.uomName ?? '';
                  const allocTone =
                    allocated > remaining
                      ? 'text-(--state-error-base)'
                      : allocated === remaining && allocated > 0
                        ? 'text-(--state-success-base)'
                        : 'text-text-strong-950';
                  return (
                    <div
                      key={sku}
                      className="flex flex-col gap-2 rounded-lg border border-stroke-soft-200 p-3"
                      data-testid={`gr-sku-${sku}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
                        <div className="min-w-0">
                          <p className={codeCls}>{sku}</p>
                          <p className={`${metaCls} truncate`}>
                            {poLine?.itemName}
                          </p>
                        </div>
                        <dl className="flex gap-5 text-right">
                          <div>
                            <dt className={metaCls}>Qty PO</dt>
                            <dd className="text-sm tabular-nums text-text-strong-950">
                              {fmtQty(poLine?.qty ?? 0)} {uom}
                            </dd>
                          </div>
                          <div>
                            <dt className={metaCls}>Sudah diterima</dt>
                            <dd className="text-sm tabular-nums text-text-strong-950">
                              {fmtQty(poLine?.qtyReceived ?? 0)} {uom}
                            </dd>
                          </div>
                          <div>
                            <dt className={metaCls}>Diterima sekarang</dt>
                            <dd
                              className={`text-sm font-medium tabular-nums ${allocTone}`}
                              data-testid={`gr-allocated-${sku}`}
                            >
                              {fmtQty(allocated)} / {fmtQty(remaining)} {uom}
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div
                        className={`${grid} text-[11px] font-medium text-text-soft-400`}
                      >
                        <span className="text-right">Qty diterima</span>
                        <span>Expired date</span>
                        <span>Storage tujuan</span>
                        <span />
                      </div>
                      {skuLines.map((l) => {
                        const i = lines.indexOf(l);
                        // Several rows of one SKU need names of their own.
                        const lineName =
                          skuLines.length > 1 ? `${l.sku} #${i + 1}` : l.sku;
                        const lineErr = lineErrorOf(l.key, null);
                        const expiryErr = lineErrorOf(l.key, 'expiredDate');
                        return (
                          <div key={l.key} className="flex flex-col gap-1">
                            <div
                              className={`${grid} rounded-md ${
                                lineErr || expiryErr
                                  ? 'outline outline-1 outline-(--state-error-base)'
                                  : ''
                              }`}
                            >
                              <TextInput
                                aria-label={`Qty diterima ${lineName}`}
                                type="number"
                                size="md"
                                min={0}
                                step="any"
                                value={l.qty}
                                onChange={(v) => setLine(l.key, { qty: v })}
                                className="text-right"
                              />
                              <div className="min-w-0">
                                <DateField
                                  aria-label={`Expired date ${lineName}`}
                                  data-testid={`gr-expiry-${i}`}
                                  size="md"
                                  value={l.expiry}
                                  onChange={(v) =>
                                    editLine(l.key, { expiry: v })
                                  }
                                  invalid={!!expiryErr}
                                  aria-describedby={
                                    expiryErr
                                      ? `gr-expiry-err-${l.key}`
                                      : undefined
                                  }
                                />
                                {expiryErr && (
                                  <InlineError id={`gr-expiry-err-${l.key}`}>
                                    {expiryErr}
                                  </InlineError>
                                )}
                              </div>
                              <StorageSelect
                                aria-label={`Storage tujuan ${lineName}`}
                                testId={`gr-storage-${i}`}
                                hubID={po.hubID}
                                value={l.storageID}
                                onChange={(id) =>
                                  editLine(l.key, { storageID: id })
                                }
                                labels={labels}
                              />
                              <div className="flex h-9 items-center justify-end">
                                {skuLines.length > 1 && (
                                  <Button
                                    tone="neutral"
                                    style="ghost"
                                    size="icon-sm"
                                    title="Hapus baris"
                                    aria-label={`Hapus baris ${lineName}`}
                                    onClick={() =>
                                      setLines((p) =>
                                        p.filter((x) => x.key !== l.key)
                                      )
                                    }
                                  >
                                    <i className="ri-delete-bin-line text-[14px]" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            {lineErr && (
                              <InlineError>
                                {l.sku}: {lineErr}
                              </InlineError>
                            )}
                          </div>
                        );
                      })}
                      <div>
                        <Button
                          tone="neutral"
                          style="ghost"
                          size="xs"
                          aria-label={`Tambah storage ${sku}`}
                          data-testid={`gr-add-storage-${sku}`}
                          onClick={() => addStorage(sku)}
                          leftIcon={<i className="ri-add-line text-[14px]" />}
                        >
                          Tambah storage
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {step === 3 && (
            <Section title="Penerimaan">
              <div className="flex flex-col gap-3">
                {errorAlert}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    label="Date arrival"
                    htmlFor="gr-arrival"
                    error={fe('arrival')}
                  >
                    <TextInput
                      id="gr-arrival"
                      type="datetime-local"
                      size="md"
                      value={arrivedAt}
                      onChange={setArrivedAt}
                    />
                  </FormField>
                  <FormField
                    label="No. surat jalan supplier"
                    htmlFor="gr-dn"
                    error={fe('deliveryNoteNo')}
                  >
                    <TextInput
                      id="gr-dn"
                      size="md"
                      value={deliveryNoteNo}
                      onChange={setDeliveryNoteNo}
                      className="font-mono"
                    />
                  </FormField>
                </div>
                <FormField
                  label="Surat jalan supplier (PDF)"
                  required
                  error={fe('deliveryNote')}
                >
                  <DocumentUpload
                    value={deliveryNote}
                    onChange={setDeliveryNote}
                    onBusyChange={setUploadingDoc}
                    testId="gr-delivery-note"
                  />
                </FormField>
                <FormField
                  label="Catatan"
                  htmlFor="gr-notes"
                  optional
                  error={fe('notes')}
                >
                  <TextArea id="gr-notes" value={notes} onChange={setNotes} />
                </FormField>
                <label className="flex items-start gap-2 text-[12px] text-text-sub-600">
                  <Checkbox
                    checked={closeBackorder}
                    onCheckedChange={(v) => setCloseBackorder(v === true)}
                    className="mt-0.5"
                  />
                  <span>Tutup sisa PO (cancel backorder)</span>
                </label>
                {fe('closesBackorder') && (
                  <p
                    className="text-[11px] text-(--state-error-base)"
                    role="alert"
                  >
                    {fe('closesBackorder')}
                  </p>
                )}
              </div>
            </Section>
          )}

          {step === 4 && (
            <Section
              title="Review"
              description={`${po.code} · ${po.supplierName}`}
            >
              <div className="flex flex-col gap-4" data-testid="gr-review">
                {errorAlert}
                <KVGrid
                  items={[
                    [
                      'Date arrival',
                      arrivedAt
                        ? fmtDateTime(new Date(arrivedAt).toISOString())
                        : null,
                    ],
                    ['No. surat jalan supplier', deliveryNoteNo.trim() || null],
                    [
                      'Surat jalan supplier (PDF)',
                      deliveryNote ? (
                        <a
                          key="dn"
                          href={deliveryNote.url}
                          target="_blank"
                          rel="noreferrer"
                          className={linkCls}
                        >
                          {deliveryNote.name}
                        </a>
                      ) : null,
                    ],
                    ['Catatan', notes.trim() || null],
                  ]}
                />
                <div className="flex flex-col divide-y divide-stroke-soft-200 rounded-lg border border-stroke-soft-200">
                  {filled.map((l) => {
                    const poLine = lineBy.get(l.sku);
                    const dest = labels.byID.get(l.storageID);
                    return (
                      <div
                        key={l.key}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className={codeCls}>{l.sku}</p>
                          <p className={`${metaCls} truncate`}>
                            {dest ? storageLabel(dest) : '—'}
                            {l.expiry
                              ? ` · exp ${fmtDate(l.expiry)}`
                              : ' · tanpa expiry'}
                          </p>
                        </div>
                        <span className="text-[13px] tabular-nums text-text-strong-950">
                          {fmtQty(Number(l.qty))} {poLine?.uomName}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {remainingAfter.length > 0 && (
                  <Alert
                    status={closeBackorder ? 'warning' : 'information'}
                    appearance="stroke"
                    size="lg"
                    title={
                      closeBackorder ? 'Sisa PO ditutup' : 'Sisa PO tetap open'
                    }
                  >
                    {remainingAfter.map((x, i) => (
                      <span key={x.sku}>
                        {i > 0 && ' · '}
                        <span className="font-mono">{x.sku}</span> sisa{' '}
                        {fmtQty(x.left)} {x.uom}
                      </span>
                    ))}
                  </Alert>
                )}
              </div>
            </Section>
          )}

          <WizardFooter
            step={step}
            total={STEPS.length}
            onBack={back}
            onNext={next}
            onSubmit={() => void submit()}
            submitLabel="Submit goods receipt"
            submitTestId="gr-submit"
            busy={busy}
            nextDisabled={(step === 2 && !lines.length) || uploadingDoc}
            nextDisabledReason={
              uploadingDoc
                ? 'Menunggu upload selesai'
                : 'Semua qty PO sudah diterima'
            }
          />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Info">
            <KV
              label="Client"
              value={po.client?.name ?? `Client #${po.clientID}`}
            />
            <PoSourceKVs po={po} />
            <KV label="Hub" value={po.hub?.name ?? `Hub #${po.hubID}`} />
            <KV label="Est. arrival" value={fmtDateTime(po.estArrivalAt)} />
            <KV
              label="Progress"
              value={
                <ProgressCell
                  received={sum(detail.lines.map((l) => l.qtyReceived))}
                  expected={sum(detail.lines.map((l) => l.qty))}
                  format={fmtQty}
                />
              }
            />
          </Section>
        </div>
      </div>
    </div>
  );
};

const GoodsReceiptNewPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const poID = params.get('po');
  const detail = useLoad(
    (signal) => warehouseApi.getInboundRequest(poID as string, { signal }),
    [poID],
    !!poID
  );

  if (poID && detail.loading && !detail.data) return <DetailSkeleton />;

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.goodsReceipt}
        backLabel="Goods receipt"
        title="New goods receipt"
        badges={
          detail.data ? (
            <Tag size="sm" variant="gray">
              {detail.data.order.code}
            </Tag>
          ) : undefined
        }
        actions={
          poID ? (
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new`)}
            >
              Ganti inbound
            </Button>
          ) : undefined
        }
      />
      {!poID ? (
        <div className="flex flex-col gap-5">
          <WizardSteps steps={STEPS} current={1} />
          <PickInbound />
        </div>
      ) : detail.error || !detail.data ? (
        <LoadError
          message={detail.error?.message ?? 'Inbound request tidak ditemukan'}
          onRetry={detail.reload}
        />
      ) : detail.data.order.status !== 'CONFIRMED' ||
        detail.data.order.receiptStatus === 'FINISHED' ? (
        <Alert
          status="information"
          appearance="stroke"
          size="lg"
          title="Tidak bisa menerima barang"
        >
          {detail.data.order.code} berstatus{' '}
          {detail.data.order.receiptStatus === 'FINISHED'
            ? 'Finished'
            : detail.data.order.status}
          .
        </Alert>
      ) : (
        <ReceiptForm key={detail.data.order.id} detail={detail.data} />
      )}
    </div>
  );
};

export default GoodsReceiptNewPage;
